import api from './api';

export interface SystemSettings {
  company: CompanySettings;
  workHours: WorkHoursSettings;
  leave: LeaveSettings;
  notifications: NotificationSettings;
  system: SystemGeneralSettings;
}

export interface CompanySettings {
  CompanyName: string;
  CompanyAddress: string;
  CompanyPhone: string;
  CompanyEmail: string;
  CompanyWebsite: string;
  TaxNumber: string;
  TaxOffice: string;
  Logo: string;
}

export interface WorkHoursSettings {
  WorkStartTime: string;
  WorkEndTime: string;
  BreakStartTime: string;
  BreakEndTime: string;
  LateToleranceMinutes: string;
  EarlyLeaveToleranceMinutes: string;
  MinReadingIntervalMinutes: string;
  OnlyFirstLastEntry: string;
}

export interface LeaveSettings {
  AnnualLeaveDays: string;
  SickLeaveDays: string;
  RequireApproval: string;
}

export interface NotificationSettings {
  EmailNotifications: string;
  SMSNotifications: string;
  LateNotifications: string;
  AbsenceNotifications: string;
  // Mail Server Settings
  SmtpHost: string;
  SmtpPort: string;
  SmtpUsername: string;
  SmtpPassword: string;
  SmtpUseSsl: string;
  SmtpFromEmail: string;
  SmtpFromName: string;
  // SMS API Settings
  SmsProvider: string;
  SmsApiKey: string;
  SmsApiSecret: string;
  SmsSenderId: string;
}

export interface SystemGeneralSettings {
  Language: string;
  Timezone: string;
  DateFormat: string;
  TimeFormat: string;
  Currency: string;
  SessionTimeout: string;
  DeviceSyncEnabled: string;
  DeviceSyncTime: string;
  DeviceSyncTime1: string;
  DeviceSyncTime2: string;
  DuplicateFilterMinutes: string;
}

export interface SettingUpdateDto {
  [group: string]: {
    [key: string]: string;
  };
}

// Varsayilan ayarlar
export const DEFAULT_SETTINGS: SystemSettings = {
  company: {
    CompanyName: 'TempusPro',
    CompanyAddress: '',
    CompanyPhone: '',
    CompanyEmail: '',
    CompanyWebsite: '',
    TaxNumber: '',
    TaxOffice: '',
    Logo: ''
  },
  workHours: {
    WorkStartTime: '08:30',
    WorkEndTime: '17:30',
    BreakStartTime: '12:00',
    BreakEndTime: '13:00',
    LateToleranceMinutes: '15',
    EarlyLeaveToleranceMinutes: '15',
    MinReadingIntervalMinutes: '2',
    OnlyFirstLastEntry: 'false'
  },
  leave: {
    AnnualLeaveDays: '14',
    SickLeaveDays: '10',
    RequireApproval: 'true'
  },
  notifications: {
    EmailNotifications: 'true',
    SMSNotifications: 'false',
    LateNotifications: 'true',
    AbsenceNotifications: 'true',
    // Mail Server Settings
    SmtpHost: '',
    SmtpPort: '587',
    SmtpUsername: '',
    SmtpPassword: '',
    SmtpUseSsl: 'true',
    SmtpFromEmail: '',
    SmtpFromName: 'TempusPro',
    // SMS API Settings
    SmsProvider: 'netgsm',
    SmsApiKey: '',
    SmsApiSecret: '',
    SmsSenderId: ''
  },
  system: {
    Language: 'tr',
    Timezone: 'Europe/Istanbul',
    DateFormat: 'dd.MM.yyyy',
    TimeFormat: 'HH:mm',
    Currency: 'TRY',
    SessionTimeout: '30',
    DeviceSyncEnabled: 'true',
    DeviceSyncTime: '18:00',
    DeviceSyncTime1: '07:00',
    DeviceSyncTime2: '18:00',
    DuplicateFilterMinutes: '5'
  }
};

const settingsService = {
  // Tum ayarlari getir
  getAll: async (): Promise<SystemSettings> => {
    const response = await api.get<SystemSettings>('/settings');
    return { ...DEFAULT_SETTINGS, ...response.data };
  },

  // Belirli bir grubu getir
  getByGroup: async <T>(group: string): Promise<T> => {
    const response = await api.get<T>(`/settings/${group}`);
    return response.data;
  },

  // Ayarlari guncelle
  update: async (settings: SettingUpdateDto): Promise<void> => {
    await api.put('/settings', settings);
  },

  // Tek bir ayari guncelle
  updateSingle: async (group: string, key: string, value: string, description?: string): Promise<void> => {
    await api.put(`/settings/${group}/${key}`, { value, description });
  },

  // Logo yukle
  uploadLogo: async (logoBase64: string): Promise<void> => {
    await api.post('/settings/logo', { logoBase64 });
  }
};

export default settingsService;
